/**
 * Account home: who you are, what remains, CVs you can reopen, then settings.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  SUPPORT_EMAIL,
  PRIVACY_EMAIL,
  buildMailto,
} from '../config/support';
import Seo from '../components/Seo';
import FeedbackModal from '../components/FeedbackModal';
import { APP_HOME, FROM_FILE, KEEP_LOOK, KEEP_LOOK_ENABLED, REWRITE } from '../utils/paths';
import { identityLabel, usableDisplayName } from '../utils/displayName';
import {
  COOKIE_CONSENT_EVENT,
  getCookiePreferences,
  openCookieBanner,
} from '../utils/cookieConsent';
import {
  accountInitials,
  downloadResumePath,
  freeRemainders,
  libraryHeading,
  remainingCopyId,
  scoreBand,
  workKind,
} from '../utils/accountPlan';
import './Settings.css';

const DELETE_ERROR_KEYS = {
  confirmation_required: 'account.deleteErrConfirmation',
  confirmation_mismatch: 'account.deleteErrMismatch',
  password_required: 'account.deleteErrPasswordRequired',
  password_incorrect: 'account.deleteErrPassword',
  staff_cannot_delete: 'account.deleteErrStaff',
  stripe_failed: 'account.deleteErrStripe',
  cancel_pro_first: 'account.deleteErrCancelPro',
  rate_limited: 'login.errRateLimited',
};

const formatWhen = (iso, lang) => {
  if (!iso) {
    return '';
  }
  try {
    return new Date(iso).toLocaleString(lang === 'en' ? 'en-GB' : 'es-ES', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const formatPeriodEnd = (iso, lang) => {
  if (!iso) {
    return '';
  }
  try {
    return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const firstDjoserError = (data) => {
  if (!data || typeof data !== 'object') {
    return '';
  }
  const keys = ['current_password', 'new_password', 're_new_password', 'non_field_errors'];
  for (let i = 0; i < keys.length; i += 1) {
    const val = data[keys[i]];
    if (Array.isArray(val) && val[0]) {
      return String(val[0]);
    }
    if (typeof val === 'string' && val) {
      return val;
    }
  }
  return '';
};

const QuotaMeter = ({
  label,
  used,
  limit,
  unlimitedLabel,
  usedOfTemplate,
  lockedLabel,
}) => {
  if (limit == null) {
    return (
      <div className="account-meter">
        <div className="account-meter__row">
          <span>{label}</span>
          <span className="account-meter__value">{unlimitedLabel}</span>
        </div>
      </div>
    );
  }
  if (lockedLabel && Number(limit) <= 0) {
    return (
      <div className="account-meter is-full">
        <div className="account-meter__row">
          <span>{label}</span>
          <span className="account-meter__value">{lockedLabel}</span>
        </div>
      </div>
    );
  }
  const safeUsed = Number(used) || 0;
  const safeLimit = Number(limit) || 0;
  const pct = safeLimit <= 0 ? 0 : Math.min(100, Math.round((safeUsed / safeLimit) * 100));
  const exhausted = safeUsed >= safeLimit;
  const usedLabel = usedOfTemplate
    .replace('{used}', String(safeUsed))
    .replace('{limit}', String(safeLimit));
  return (
    <div className={`account-meter${exhausted ? ' is-full' : ''}`}>
      <div className="account-meter__row">
        <span>{label}</span>
        <span className="account-meter__value">{usedLabel}</span>
      </div>
      <div className="account-meter__track" aria-hidden="true">
        <div className="account-meter__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

QuotaMeter.propTypes = {
  label: PropTypes.string.isRequired,
  used: PropTypes.number,
  limit: PropTypes.number,
  unlimitedLabel: PropTypes.string.isRequired,
  usedOfTemplate: PropTypes.string.isRequired,
  lockedLabel: PropTypes.string,
};

QuotaMeter.defaultProps = {
  used: 0,
  limit: null,
  lockedLabel: '',
};

const mediaUrl = (url) => {
  const src = String(url || '').trim();
  if (!src) {
    return '';
  }
  if (/^https?:\/\//i.test(src)) {
    return src;
  }
  if (src.startsWith('/media/')) {
    const base = (
      axios.defaults.baseURL ||
      process.env.REACT_APP_API_URL ||
      'http://localhost:8000'
    ).replace(/\/$/, '');
    return `${base}${src}`;
  }
  return src;
};

const DocPreview = ({ item }) => {
  const kind = workKind(item);
  if (kind === 'report' && item.overall_score != null) {
    const band = scoreBand(item.overall_score);
    return (
      <div className={`account-doc__preview account-doc__preview--report is-${band}`}>
        <span className="account-doc__score">{item.overall_score}</span>
        <span className="account-doc__preview-label">ATS</span>
      </div>
    );
  }
  const thumb = mediaUrl(item.thumbnail_url);
  if (thumb) {
    return (
      <div className="account-doc__preview account-doc__preview--thumb">
        <img src={thumb} alt="" />
      </div>
    );
  }
  const fmt = String(item.output_format || item.file_kind || 'pdf').toUpperCase();
  const badge = fmt === 'DOC' || fmt === 'DOCX' ? 'WORD' : fmt || 'PDF';
  return (
    <div className="account-doc__preview account-doc__preview--file">
      <span className="account-doc__file-badge">{badge}</span>
    </div>
  );
};

DocPreview.propTypes = {
  item: PropTypes.object.isRequired,
};

const Settings = () => {
  const { user, isPro, cancelAtPeriodEnd, currentPeriodEnd, deleteAccount } = useAuth();
  const { t, lang, localePath } = useLanguage();
  const navigate = useNavigate();

  const [showDelete, setShowDelete] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [quota, setQuota] = useState(null);
  const [library, setLibrary] = useState([]);
  const [statsReady, setStatsReady] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [reNewPassword, setReNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cookiePrefs, setCookiePrefs] = useState(() => getCookiePreferences());
  const confirmInputRef = useRef(null);

  const hasPassword = user?.has_password !== false;
  const mustCancelPro = Boolean(isPro && !cancelAtPeriodEnd);
  const displayName = usableDisplayName(user);
  const helloName = identityLabel(user) || t('account.helloFallback');
  const planLabel = isPro
    ? cancelAtPeriodEnd
      ? t('account.planEnding')
      : t('account.planPro')
    : t('account.planFree');
  const periodLabel = formatPeriodEnd(currentPeriodEnd, lang);
  const remainders = freeRemainders(quota);
  const copyId = remainingCopyId(remainders);
  const canDownloadNow = Boolean(isPro || remainders.pdfCredits > 0);

  const supportHref = buildMailto({
    email: SUPPORT_EMAIL,
    subject: t('account.helpSubject'),
  });
  const exportHref = buildMailto({
    email: PRIVACY_EMAIL,
    subject: t('account.exportSubject'),
    body: t('account.exportBody').replace('{email}', user?.email || ''),
  });

  let planSentence = t('account.quotaWindow');
  if (isPro) {
    if (periodLabel) {
      planSentence = cancelAtPeriodEnd
        ? t('account.planUntil').replace('{date}', periodLabel)
        : t('account.planRenews').replace('{date}', periodLabel);
    } else {
      planSentence = t('account.planHintPro');
    }
  } else if (statsReady) {
    const remainingKeys = {
      both: 'account.remainingBoth',
      ats: 'account.remainingAts',
      studio: 'account.remainingStudio',
      none: 'account.remainingNone',
    };
    planSentence = t(remainingKeys[copyId])
      .replace('{ats}', String(remainders.atsLeft))
      .replace('{studio}', String(remainders.studioLeft));
  }

  let cookieLabel = t('account.cookiesUnset');
  if (cookiePrefs?.sessionReplay) {
    cookieLabel = t('account.cookiesAnalyticsReplay');
  } else if (cookiePrefs?.analytics) {
    cookieLabel = t('account.cookiesAnalytics');
  } else if (cookiePrefs) {
    cookieLabel = t('account.cookiesNecessary');
  }

  useEffect(() => {
    let cancelled = false;
    const loadStats = async () => {
      try {
        const { data } = await axios.get('/api/cvs/stats/');
        if (cancelled) {
          return;
        }
        setQuota(data.quota || null);
        setLibrary(Array.isArray(data.library) ? data.library : []);
      } catch (error) {
        if (!cancelled) {
          setQuota(null);
          setLibrary([]);
        }
      } finally {
        if (!cancelled) {
          setStatsReady(true);
        }
      }
    };
    loadStats();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncCookies = () => setCookiePrefs(getCookiePreferences());
    window.addEventListener(COOKIE_CONSENT_EVENT, syncCookies);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, syncCookies);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (deleteLoading) {
      return;
    }
    setShowDelete(false);
    setConfirmation('');
    setPassword('');
    setDeleteError('');
  }, [deleteLoading]);

  useEffect(() => {
    if (!showDelete) {
      return undefined;
    }
    confirmInputRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !deleteLoading) {
        closeDeleteModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showDelete, deleteLoading, closeDeleteModal]);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await axios.post('/api/cvs/create-billing-portal-session/');
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error(t('pricing.portalError'));
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t('pricing.portalError');
      toast.error(msg);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    if (newPassword.length < 8) {
      setPasswordError(t('login.errWeakPassword'));
      return;
    }
    if (newPassword !== reNewPassword) {
      setPasswordError(t('login.errPasswordMatch'));
      return;
    }
    setPasswordLoading(true);
    setPasswordError('');
    try {
      await axios.post('/api/auth/users/set_password/', {
        current_password: currentPassword,
        new_password: newPassword,
        re_new_password: reNewPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setReNewPassword('');
      setShowPasswordForm(false);
      toast.success(t('account.passwordDone'));
    } catch (err) {
      const mapped = firstDjoserError(err.response?.data);
      if (err.response?.status === 400 && /current|invalid password/i.test(mapped)) {
        setPasswordError(t('account.passwordWrong'));
      } else {
        setPasswordError(mapped || t('account.passwordError'));
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSendPasswordLink = async () => {
    if (!user?.email) {
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(
        '/api/auth/users/reset_password/',
        { email: user.email },
        {
          headers: {
            'Accept-Language': lang === 'en' ? 'en' : 'es',
            'X-ClarCV-Lang': lang === 'en' ? 'en' : 'es',
          },
        }
      );
      toast.success(t('account.passwordLinkSent'));
    } catch (err) {
      if (err.response?.status === 204 || err.response?.status === 200 || !err.response) {
        toast.success(t('account.passwordLinkSent'));
      } else {
        toast.success(t('account.passwordLinkSent'));
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleDelete = async () => {
    const typed = confirmation.trim();
    if (!typed) {
      setDeleteError(t('account.deleteErrConfirmation'));
      return;
    }
    if (typed.toLowerCase() !== (user?.email || '').toLowerCase()) {
      setDeleteError(t('account.deleteErrMismatch'));
      return;
    }
    if (hasPassword && !password) {
      setDeleteError(t('account.deleteErrPasswordRequired'));
      return;
    }
    setDeleteLoading(true);
    setDeleteError('');
    try {
      const result = await deleteAccount({ confirmation: typed, password });
      if (result.success) {
        toast.success(t('account.deleteDone'));
        navigate('/', { replace: true });
        return;
      }
      const key = DELETE_ERROR_KEYS[result.errorCode];
      setDeleteError(key ? t(key) : t('account.deleteError'));
    } catch (err) {
      setDeleteError(t('account.deleteError'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const pdfCreditLabel =
    remainders.pdfCredits === 1
      ? t('account.creditPdf')
      : t('account.creditPdfs').replace('{n}', String(remainders.pdfCredits));
  const atsCreditLabel =
    remainders.atsCredits === 1
      ? t('account.creditAts')
      : t('account.creditAtsMany').replace('{n}', String(remainders.atsCredits));

  return (
    <div className="account-page">
      <Seo title={t('account.title')} description={t('account.lead')} path="/dashboard/settings" noindex />
      <div className="container account-page__inner">
        <section className="account-hero" aria-labelledby="account-hello">
          <div className="account-hero__who">
            <span className="account-hero__avatar" aria-hidden="true">
              {accountInitials(displayName || helloName)}
            </span>
            <div>
              <h1 id="account-hello">{t('dashboard.hello').replace('{name}', helloName)}</h1>
              <p className="account-hero__email">{user?.email || '—'}</p>
            </div>
          </div>
          <div className="account-hero__plan">
            <p className="account-plan-row">
              <span className={isPro ? 'account-badge account-badge--pro' : 'account-badge'}>
                {planLabel}
              </span>
            </p>
            <p className="account-hero__status">{planSentence}</p>
            {!isPro && statsReady && (remainders.pdfCredits > 0 || remainders.atsCredits > 0) ? (
              <ul className="account-credits">
                {remainders.pdfCredits > 0 ? <li>{pdfCreditLabel}</li> : null}
                {remainders.atsCredits > 0 ? <li>{atsCreditLabel}</li> : null}
              </ul>
            ) : null}
            <div className="account-hero__actions">
              {isPro ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={portalLoading}
                    onClick={handleManageBilling}
                  >
                    {portalLoading ? t('account.billingLoading') : t('account.manageBilling')}
                  </button>
                  {!cancelAtPeriodEnd ? (
                    <Link to={localePath('/pricing')} className="btn btn-outline">
                      {t('account.cancelOnPricing')}
                    </Link>
                  ) : null}
                </>
              ) : (
                <Link to={localePath('/pricing')} className="btn btn-primary">
                  {copyId === 'none' ? t('account.upgradeNow') : t('account.upgrade')}
                </Link>
              )}
            </div>
          </div>
        </section>

        <section className="account-card" aria-labelledby="account-library">
          <div className="account-card__head">
            <h2 id="account-library">{t('account.libraryTitle')}</h2>
            <Link to={APP_HOME} className="account-activity__panel">
              {t('account.libraryNew')}
            </Link>
          </div>
          <p className="account-card__hint">
            {isPro ? t('account.libraryLeadPro') : t('account.libraryLead')}
          </p>
          {library.length === 0 ? (
            <div className="account-activity__empty-box">
              <p>{t('account.libraryEmpty')}</p>
              <Link to={APP_HOME} className="btn btn-ghost">
                {t('account.libraryEmptyCta')}
              </Link>
            </div>
          ) : (
            <ul className="account-docs">
              {library.map((item) => {
                const kind = workKind(item);
                const heading = libraryHeading(item) || t('account.libraryUntitled');
                const when = formatWhen(item.created_at, lang);
                const fmt = String(item.output_format || item.file_kind || '').toUpperCase();
                const resumeTo = downloadResumePath(item.flow, {
                  keepLook: KEEP_LOOK_ENABLED ? KEEP_LOOK : REWRITE,
                  rewrite: REWRITE,
                  rewrite: REWRITE,
                  editor: '/dashboard/edit-cv',
                  fromFile: FROM_FILE,
                });
                let eyebrow = t('account.docUpload');
                if (kind === 'report') {
                  eyebrow = item.is_teaser ? t('account.docReportTeaser') : t('account.docReportFull');
                } else if (kind === 'download') {
                  eyebrow = fmt === 'DOCX' || fmt === 'DOC' ? t('account.docWord') : t('account.docPdf');
                }
                const metaParts = [
                  when,
                  kind === 'report' && item.overall_score != null
                    ? t('account.libraryScore').replace('{n}', String(item.overall_score))
                    : '',
                  kind === 'download' ? fmt : '',
                  item.template_name && kind !== 'download' ? item.template_name : '',
                ].filter(Boolean);
                return (
                  <li
                    key={item.id}
                    className={`account-doc${item.is_teaser ? ' is-teaser' : ''}${kind === 'download' ? ' account-doc--file' : ''}`}
                  >
                    <DocPreview item={item} />
                    <div className="account-doc__body">
                      <p className="account-doc__eyebrow">{eyebrow}</p>
                      <p className="account-doc__title">{heading}</p>
                      <p className="account-doc__meta">{metaParts.join(' · ')}</p>
                      {item.is_teaser ? (
                        <p className="account-doc__lock">{t('account.docTeaserHint')}</p>
                      ) : null}
                      {kind === 'download' && !canDownloadNow ? (
                        <p className="account-doc__lock">{t('account.docPdfPayHint')}</p>
                      ) : null}
                      <div className="account-doc__actions">
                        {kind === 'report' && item.cv_id ? (
                          <Link to={`/dashboard/cv/${item.cv_id}`} className="account-activity__link">
                            {item.is_teaser ? t('account.libraryOpenTeaser') : t('account.libraryOpenAts')}
                          </Link>
                        ) : null}
                        {kind === 'report' && item.is_teaser ? (
                          <Link to={localePath('/pricing')} className="account-activity__link">
                            {t('account.docUnlock')}
                          </Link>
                        ) : null}
                        {item.has_file && item.cv_id ? (
                          <Link
                            to={`${REWRITE}?fromCv=${encodeURIComponent(String(item.cv_id))}`}
                            className="account-activity__link"
                          >
                            {t('account.libraryRewrite')}
                          </Link>
                        ) : null}
                        {kind === 'download' && canDownloadNow ? (
                          <Link to={resumeTo} className="account-activity__link">
                            {t('account.docMakeAnother')}
                          </Link>
                        ) : null}
                        {kind === 'download' && !canDownloadNow ? (
                          <Link to={localePath('/pricing')} className="account-activity__link">
                            {t('account.docPayDownload')}
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <details className="account-card account-details">
          <summary>{t('account.quotaDetail')}</summary>
          <div className="account-meters">
            <QuotaMeter
              label={t('account.quotaAnalyses')}
              used={quota?.analyses?.used ?? 0}
              limit={isPro ? null : quota?.analyses?.limit ?? 1}
              unlimitedLabel={t('account.quotaUnlimited')}
              usedOfTemplate={t('account.quotaUsedOf')}
            />
            <QuotaMeter
              label={t('account.quotaStudio')}
              used={quota?.studio_extracts?.used ?? 0}
              limit={isPro ? null : quota?.studio_extracts?.limit ?? 1}
              unlimitedLabel={t('account.quotaUnlimited')}
              usedOfTemplate={t('account.quotaUsedOf')}
            />
            <QuotaMeter
              label={t('account.quotaDownloads')}
              used={quota?.downloads?.used ?? 0}
              limit={isPro ? null : quota?.downloads?.limit ?? 0}
              unlimitedLabel={t('account.quotaUnlimited')}
              usedOfTemplate={t('account.quotaUsedOf')}
              lockedLabel={t('account.quotaDownloadPaywall')}
            />
            <QuotaMeter
              label={t('account.quotaDesign')}
              used={quota?.design_prompts?.used ?? 0}
              limit={isPro ? null : quota?.design_prompts?.limit ?? 0}
              unlimitedLabel={t('account.quotaUnlimited')}
              usedOfTemplate={t('account.quotaUsedOf')}
              lockedLabel={t('account.quotaProOnly')}
            />
            <QuotaMeter
              label={t('account.quotaLocalize')}
              used={quota?.localizations?.used ?? 0}
              limit={quota?.localizations?.limit ?? null}
              unlimitedLabel={t('account.quotaUnlimited')}
              usedOfTemplate={t('account.quotaUsedOf')}
            />
          </div>
        </details>

        <section className="account-card" aria-labelledby="account-security">
          <h2 id="account-security">{t('account.signInAndPrivacy')}</h2>
          <dl className="account-dl">
            <div>
              <dt>{t('account.signIn')}</dt>
              <dd>{hasPassword ? t('account.signInPassword') : t('account.signInGoogle')}</dd>
            </div>
            <div>
              <dt>{t('account.cookiesStatus')}</dt>
              <dd>{cookieLabel}</dd>
            </div>
          </dl>

          {hasPassword ? (
            <>
              <p className="account-card__hint">{t('account.passwordHintLoggedIn')}</p>
              {showPasswordForm ? (
                <form className="account-password" onSubmit={handleChangePassword}>
                  <label className="account-modal__label" htmlFor="account-current-password">
                    {t('account.passwordCurrent')}
                  </label>
                  <input
                    id="account-current-password"
                    type="password"
                    data-clarity-mask="true"
                    autoComplete="current-password"
                    value={currentPassword}
                    disabled={passwordLoading}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                  />
                  <label className="account-modal__label" htmlFor="account-new-password">
                    {t('account.passwordNew')}
                  </label>
                  <input
                    id="account-new-password"
                    type="password"
                    data-clarity-mask="true"
                    autoComplete="new-password"
                    value={newPassword}
                    disabled={passwordLoading}
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                  <label className="account-modal__label" htmlFor="account-re-password">
                    {t('account.passwordRepeat')}
                  </label>
                  <input
                    id="account-re-password"
                    type="password"
                    data-clarity-mask="true"
                    autoComplete="new-password"
                    value={reNewPassword}
                    disabled={passwordLoading}
                    onChange={(event) => setReNewPassword(event.target.value)}
                  />
                  {passwordError ? (
                    <p className="account-modal__error" role="alert">
                      {passwordError}
                    </p>
                  ) : null}
                  <div className="account-password__actions">
                    <button type="submit" className="btn btn-primary" disabled={passwordLoading}>
                      {passwordLoading ? t('account.passwordSaving') : t('account.passwordSave')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={passwordLoading}
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordError('');
                      }}
                    >
                      {t('account.passwordCancel')}
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowPasswordForm(true)}
                >
                  {t('account.passwordChange')}
                </button>
              )}
            </>
          ) : (
            <>
              <p className="account-card__hint">{t('account.googlePasswordAction')}</p>
              <button
                type="button"
                className="btn btn-outline"
                disabled={resetLoading}
                onClick={handleSendPasswordLink}
              >
                {resetLoading ? t('account.passwordSaving') : t('account.passwordSendLink')}
              </button>
            </>
          )}

          <ul className="account-tools">
            <li>
              <button type="button" className="account-tools__btn" onClick={() => openCookieBanner()}>
                {t('account.cookiesAction')}
              </button>
            </li>
            <li>
              <button type="button" className="account-tools__btn" onClick={() => setShowFeedback(true)}>
                {t('account.feedback')}
              </button>
            </li>
            <li>
              <a href={exportHref}>{t('account.exportMailto')}</a>
            </li>
            <li>
              <Link to={localePath('/privacy')}>{t('footer.privacy')}</Link>
            </li>
            <li>
              <Link to={localePath('/terms')}>{t('footer.terms')}</Link>
            </li>
            <li>
              <a href={supportHref}>{SUPPORT_EMAIL}</a>
            </li>
          </ul>
        </section>

        <section className="account-danger" aria-labelledby="account-delete">
          <div>
            <h2 id="account-delete">{t('account.deleteTitle')}</h2>
            <p>
              {mustCancelPro ? t('account.deleteHintCancelPro') : t('account.deleteHint')}
            </p>
          </div>
          {mustCancelPro ? (
            <Link to={localePath('/pricing')} className="account-page__delete-link">
              {t('account.deleteGoCancel')}
            </Link>
          ) : (
            <button
              type="button"
              className="account-page__delete-link"
              onClick={() => setShowDelete(true)}
            >
              {t('account.deleteButton')}
            </button>
          )}
        </section>
      </div>

      <FeedbackModal
        open={showFeedback}
        onClose={() => setShowFeedback(false)}
        page="/dashboard/settings"
      />

      {showDelete ? (
        <div
          className="account-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="account-delete-title"
        >
          <button
            type="button"
            className="account-modal__backdrop"
            aria-label={t('account.deleteDismiss')}
            onClick={closeDeleteModal}
          />
          <div className="account-modal__panel">
            <h2 id="account-delete-title">{t('account.deleteConfirmTitle')}</h2>
            <p>{t('account.deleteConfirmBody')}</p>
            <label className="account-modal__label" htmlFor="account-delete-email">
              {t('account.deleteTypeEmail')}
            </label>
            <input
              id="account-delete-email"
              ref={confirmInputRef}
              type="email"
              autoComplete="off"
              value={confirmation}
              disabled={deleteLoading}
              placeholder={user?.email || ''}
              onChange={(event) => setConfirmation(event.target.value)}
            />
            {hasPassword ? (
              <>
                <label className="account-modal__label" htmlFor="account-delete-password">
                  {t('account.deleteTypePassword')}
                </label>
                <input
                  id="account-delete-password"
                  type="password"
                  data-clarity-mask="true"
                  autoComplete="current-password"
                  value={password}
                  disabled={deleteLoading}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </>
            ) : (
              <p className="account-card__hint">{t('account.deleteGoogleNote')}</p>
            )}
            {deleteError ? (
              <p className="account-modal__error" role="alert">
                {deleteError}
              </p>
            ) : null}
            <div className="account-modal__actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={deleteLoading}
                onClick={closeDeleteModal}
              >
                {t('account.deleteKeep')}
              </button>
              <button
                type="button"
                className="btn account-modal__confirm"
                disabled={deleteLoading}
                onClick={handleDelete}
              >
                {deleteLoading ? t('account.deleteWorking') : t('account.deleteConfirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Settings;
